import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { findGroup, groupPath } from '../lib/groupPath';
import { useStore } from '../store/useStore';
import { Login } from './Login';

type Phase = 'joining' | 'joined' | 'failed';

/**
 * Landing page for an invite link. Signed-in visitors redeem it straight away;
 * everyone else registers first and the token rides along with the signup.
 */
export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const user = useStore((s) => s.user);
  const loadGroups = useStore((s) => s.loadGroups);
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('joining');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !token) return;

    void (async () => {
      try {
        const { groupId } = await api<{ groupId: string }>('/api/invites/accept', {
          method: 'POST',
          body: { token },
        });
        const groups = await loadGroups();
        const group = findGroup(groups, groupId);
        if (!group) throw new Error('The invite was accepted, but the team is not available.');
        setPhase('joined');
        // Brief pause so the confirmation is legible before the board appears.
        setTimeout(() => navigate(groupPath(group), { replace: true }), 900);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'That invite could not be used');
        setPhase('failed');
      }
    })();
  }, [user, token, loadGroups, navigate]);

  if (!user) return <Login inviteToken={token} />;

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="card animate-rise max-w-sm p-6 text-center">
        {phase === 'joining' && (
          <>
            <span className="mx-auto mb-3 block size-2.5 animate-pulse-dot rounded-full bg-accent" />
            <p className="text-sm text-ink-soft">Joining the team…</p>
          </>
        )}

        {phase === 'joined' && (
          <>
            <p className="text-sm font-medium text-done">You're in.</p>
            <p className="mt-1 text-sm text-ink-faint">Taking you to the board…</p>
          </>
        )}

        {phase === 'failed' && (
          <>
            <p className="text-sm font-medium text-danger">Invite could not be used</p>
            <p className="mt-1 text-sm text-ink-faint">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="btn btn-ghost mt-4 w-full"
            >
              Continue to Project-Workflow
            </button>
          </>
        )}
      </div>
    </div>
  );
}
